import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UnknownAction } from 'redux';
import { useNavigate, useParams } from 'react-router-dom';
import { PrismicRichText } from '@prismicio/react';
import styled from '@emotion/styled';

import { RootState } from 'src/shared/store/rootReducer';
import LoadingOrderDetail from './LoadingOrderDetail';
import { getDataTicket, getDataTicketlines } from '../actions';
import { ERR_GENERIC_ORDER_DETAILS } from '../../common/locale';
import { breakpoints, colors, Divider } from '../../../design-system';
import { BackButton } from '../../common/components/BackButton';
import paths from '../paths';
import accountPaths from '../paths';
import { OrderItem } from './OrderItem';
import { formatPrice } from '../../common/utils';
import { hasValidCmsText } from '../../home/utils';
import { CmsCustomTypes } from '../../cms/types';
import { loadCmsContent } from '../../cms/actions';

const StyledOrderDetailsOffLine = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;

  @media (min-width: ${breakpoints.S}px) {
    margin-top: 32px;
  }

  .order-offline-error-message {
    font-size: 1.4rem;
    font-weight: 400;
    color: ${colors.MAIN_ERROR};
  }

  .order-offline-title {
    margin: 0;
    font-size: 2rem;
    color: ${colors.BLACK};
    font-weight: 400;
  }

  .order-offline-sumup-container {
    padding: 24px;
    background-color: ${colors.BACKGROUND};
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    gap: 8px;
    p {
      margin: 0;
      font-size: 1.4rem;
      color: ${colors.BLACK};
      font-weight: 400;
      line-height: 150%;
    }
    .order-offline-sumup-status {
      margin-top: 16px;
    }
  }

  .order-offline-store-details,
  .order-offline-summary {
    h3,
    p,
    span {
      margin: 0;
      font-size: 1.4rem;
      color: ${colors.BLACK};
      font-weight: 400;
      line-height: 150%;
    }
    h3 {
      margin-bottom: 8px;
    }
    p,
    span {
      color: ${colors.MAIN_GREY};
    }
  }
  .order-offline-details {
    width: 100%;
    h3 {
      margin: 0 0 24px 0;
      font-size: 1.6rem;
      color: ${colors.BLACK};
      font-weight: 700;
    }
    @media (min-width: ${breakpoints.M}px) {
      max-width: 552px;
    }
  }

  .order-offline-summary {
    min-width: 290px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 60px;

    @media (min-width: ${breakpoints.M}px) {
      max-width: 290px;
    }
    .order-offline-summary-item {
      display: flex;
      justify-content: space-between;
    }
  }

  .order-offline-details-container {
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: 16px;

    @media (min-width: ${breakpoints.M}px) {
      flex-direction: row;
      gap: 80px;
    }
  }

  .success-color {
    p,
    span {
      color: ${colors.MAIN_SUCCESS};
    }
  }

  .bold {
    p,
    span {
      font-weight: 700;
      color: ${colors.BLACK};
    }
  }
`;

export const OrderDetailsOffLine = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ticketId, ticketDate } = useParams();
  const { dataTicket, dataTicketlines, dataTicketIsFetching, dataTicketlinesIsFetching } =
    useSelector((state: RootState) => state.account);
  const { errorType } = dataTicket ?? {};
  const { body } = useSelector((state: RootState) => state.cms.order) ?? {};

  useEffect(() => {
    if (!body?.[0]?.text) {
      dispatch(loadCmsContent(CmsCustomTypes.order) as unknown as UnknownAction);
    }
  }, []);

  useEffect(() => {
    if (ticketId && ticketDate) {
      if (!dataTicket) {
        dispatch(getDataTicket() as unknown as UnknownAction);
      }
      dispatch(getDataTicketlines(Number(ticketId), ticketDate) as unknown as UnknownAction);
    }
  }, [ticketId, ticketDate]);

  const targetedDataTicket = dataTicket?.tickets.find(
    (ticket) => ticket.id === Number(ticketId) && ticket.orderDate === ticketDate
  );

  const discount = parseFloat(
    (dataTicketlines?.items ?? [])
      .reduce((acc, item) => acc + (Number(item?.dataDiscount) || 0), 0)
      .toFixed(2)
  );

  const subTotal = parseFloat(
    (dataTicketlines?.items ?? [])
      .reduce((acc, item) => {
        const price = Number(item?.dataPrice) || 0;
        return acc + (item.returned ? -price : price);
      }, 0)
      .toFixed(2)
  );

  return (
    <StyledOrderDetailsOffLine>
      {dataTicketlinesIsFetching || dataTicketIsFetching || !dataTicket || !dataTicketlines ? (
        <LoadingOrderDetail />
      ) : errorType || !dataTicketlines?.orderNumber ? (
        <p className="order-offline-error-message">{ERR_GENERIC_ORDER_DETAILS}</p>
      ) : (
        <>
          <BackButton
            id="order-detail-back-button"
            onClick={() => navigate(paths.ACCOUNT + accountPaths.ORDERS)}
            preset="darkTheme"
            isOrderDetail
          />
          <h2 className="order-offline-title">
            {' '}
            Votre commande n° {targetedDataTicket?.posNumber}
          </h2>
          <div className="order-offline-sumup-container">
            <p>
              {`Date de votre commande : ${
                targetedDataTicket?.orderDate
                  ? new Date(targetedDataTicket.orderDate).toLocaleDateString('fr-FR')
                  : 'Date non disponible'
              }`}
            </p>
            <p>{`Numéro de votre commande : ${targetedDataTicket?.posNumber}`}</p>
            <p>{`Mode d'achat : En boutique`}</p>
            <p className="order-offline-sumup-status">{`Statut : Collectée`}</p>
          </div>
          <div className="order-offline-store-details">
            <h3>{`Boutique : ${targetedDataTicket?.store.name.toLocaleUpperCase()}`}</h3>
            <p>{targetedDataTicket?.store.address}</p>
            <p>{`${targetedDataTicket?.store.postalCode} ${targetedDataTicket?.store.city}`}</p>
            <p>{targetedDataTicket?.store.country}</p>
          </div>
          <div className="order-offline-details-container">
            <div className="order-offline-details">
              <h3>Détails de la commande</h3>
              {dataTicketlines?.items.map((item) => {
                const product = {
                  ...item,
                  rowBaseTotal: item.dataOriginalPrice,
                  rowTotal: item.dataPrice,
                };
                return (
                  <OrderItem
                    key={`${item.productRef}-${item.colorRef}`}
                    item={product}
                    returned={item.returned}
                  />
                );
              })}
            </div>
            <div className="order-offline-summary">
              <div className="order-offline-summary-item">
                <p>Sous-total</p>
                <span>{formatPrice(subTotal)}</span>
              </div>
              {discount !== 0 && (
                <div className="order-offline-summary-item success-color">
                  <p>Remise appliquée</p>
                  <span>{`-${formatPrice(Math.abs(discount))}`}</span>
                </div>
              )}
              <div>
                <Divider color={colors.LIGHT} />
              </div>
              <div className="order-offline-summary-item bold">
                <p>Total</p>
                <span>{formatPrice(Math.abs(dataTicketlines?.total))}</span>
              </div>
              {dataTicketlines?.total < 0 && (
                <div className="order-offline-summary-item success-color">
                  <p>Montant remboursé</p>
                  <span>{formatPrice(Math.abs(dataTicketlines?.total))}</span>
                </div>
              )}
            </div>
          </div>
          {hasValidCmsText(body) && (
            <div>
              <PrismicRichText field={body} />
            </div>
          )}
        </>
      )}
    </StyledOrderDetailsOffLine>
  );
};
